<script lang="ts" module>
  // face-180 has no upload and back_camera options
  export const ALLOWED_SOURCES: Source[] = [SOURCE.FRONT_CAMERA, SOURCE.COMPANION]
</script>

<script lang="ts">
  import { SOURCE, type Source } from "@/config"
  import { adjustDetectionBoundingBoxToVisibleRect } from "@/features/presets/utils"
  import {
    type BestFrameSelector,
    createBestFrameSelector,
  } from "@/libs/cv/graphs/best-frame-selection/best-frame-selection"
  import { getFeaturesStore } from "@/libs/feature-toggle"
  import type { ImageSource } from "@/libs/image"
  import { includes } from "@/libs/lodash"
  import { mix } from "@/libs/math"
  import { analytics } from "@/libs/telemetry"
  import { createPerf } from "@/libs/telemetry/performance"
  import { LoadingSplashScreen } from "@/libs/ui"
  import VideoFeed, { type Dimensions } from "@/libs/ui/VideoFeed/VideoFeed.svelte"
  import { getApplicationStore } from "@/stores/application"
  import { type ComponentProps, onDestroy } from "svelte"
  import { create as createCameraPermissionTracker } from "../../camera-permission-tracker"
  import { sendAnalytics } from "../analytics"
  import { checkFront, checkLeft, checkRight } from "../checks"
  import { type Detections, Detector } from "../detector"
  import { STAGE } from "../stages"
  import QualityCheckStages from "./QualityCheckStages"
  import { notify } from "./utils"

  const COMPLETE_DELAY = 2000

  let {
    stream,
    facing = "front",
    onCapture,
    onCaptureStart,
    onComplete,
    onError,
    onReady,
  }: {
    stream: ComponentProps<typeof VideoFeed>["src"]
    facing?: ComponentProps<typeof QualityCheckStages>["facing"]
    onReady(): void
    onComplete(): void
    onError(err: Error): void
    onCapture(
      frame: Awaited<ReturnType<NonNullable<typeof video>["getFrame"]>> & {
        metadata: {
          side: "front" | "left" | "right"
          best_frame_blur_score?: number
          best_frame_selection_enabled?: boolean
        }
      },
    ): void
    onCaptureStart(): void
  } = $props()

  const applicationStore = getApplicationStore()
  const featuresStore = getFeaturesStore()

  let bestFrameSelectionEnabled = $derived(
    $applicationStore.config.bestFrameSelection &&
      featuresStore.features.isOn("best-frame-selection"),
  )
  let bestFrameSelector = $state<BestFrameSelector>()

  let loading = $state(0)
  const detector = new Detector({
    performance: createPerf(),
    onProgress: (progress) => (loading = progress),
    initialState: {
      illumination: { intensity: 1 },
    },
    onReady: () => {
      if (bestFrameSelectionEnabled)
        createBestFrameSelector().then((selector) => {
          bestFrameSelector = selector
        })

      onReady()
    },
    onError,
  })

  let video = $state<VideoFeed>()
  let stage = $state<STAGE | null>(null)
  let detections = $state<Detections>({ face: null, illumination: null })
  let isVideoFeedActive = $state(false)

  let animationFrameId: ReturnType<typeof requestAnimationFrame> | null = null

  const cameraPermissionTracker = createCameraPermissionTracker()
  $effect(() => cameraPermissionTracker.update({ detections }))

  onDestroy(() => {
    detector.destroy()
    bestFrameSelector?.stop()
    if (animationFrameId) cancelAnimationFrame(animationFrameId)
  })

  let phase = $state(0)
  let captureTimeoutId: ReturnType<typeof setTimeout> | null = null

  $effect(() => {
    if (!stream) return

    phase = 0

    if (!bestFrameSelector || !bestFrameSelectionEnabled) return
    if (!__PROD__) console.log("[Face180] Stream changed, restarting BFS")

    bestFrameSelector.start()
  })

  const capture = () => {
    const capturingStage = (
      [STAGE.CAPTURING_FRONT, STAGE.CAPTURING_LEFT, STAGE.CAPTURING_RIGHT] as (typeof stage)[]
    )[phase]
    const capturingTimeout = [2000, 1500, 1500][phase]

    if (captureTimeoutId || !capturingStage || !capturingTimeout) return

    stage = capturingStage

    onCaptureStart()

    captureTimeoutId = setTimeout(async () => {
      if (!video) return

      const dimensions = video.getDimensions()

      if (!dimensions) return

      const bestFrame = await bestFrameSelector?.getBestFrame()
      const videoData = bestFrame
        ? { frame: await createImageBitmap(bestFrame.image), dimensions }
        : await video.getFrame()
      const side = (["front", "left", "right"] as const)[phase]

      bestFrameSelector?.stop()

      if (!__PROD__)
        console.log(
          `[LIQA] Face180: Captured ${side}` +
            (bestFrame
              ? ` with best frame blur=${((bestFrame.quality.blurScore || 0) * 1000000).toFixed(2)}`
              : ` without best frame`),
        )

      onCapture({
        ...videoData,
        metadata: {
          side,
          best_frame_blur_score: bestFrame?.quality?.blurScore,
          best_frame_selection_enabled: bestFrameSelectionEnabled,
        },
      })

      phase++
      notify()

      if (phase < 3 && bestFrameSelectionEnabled) setTimeout(() => bestFrameSelector?.start(), 100)
    }, capturingTimeout)
  }

  const cancelCapture = () => {
    if (captureTimeoutId) {
      clearTimeout(captureTimeoutId)
      captureTimeoutId = null
    }
  }

  let isLoading = $derived(loading < 1)
  const processFrame = async ({
    imageSource,
    dimensions: { width, height, visibleRect },
  }: {
    imageSource: ImageSource
    dimensions: Dimensions
  }) => {
    if (isLoading) return

    const { face, illumination } = await detector.getDetections(imageSource)

    detections = {
      ...detections,
      face: face && adjustDetectionBoundingBoxToVisibleRect(face, visibleRect, { width, height }),
      illumination: illumination
        ? {
            ...illumination,
            // Smoothening the azimuth change to avoid rapid jumps of the direction indicator
            azimuth: detections.illumination
              ? mix(
                  detections.illumination.azimuth,
                  illumination.azimuth,
                  stage === STAGE.LIGHTING_ANGLE_DEVIATION ? 0.1 : 1,
                )
              : illumination.azimuth,
          }
        : illumination,
    }

    if (includes([STAGE.CAPTURING_FRONT, STAGE.CAPTURING_LEFT, STAGE.CAPTURING_RIGHT], stage))
      await bestFrameSelector?.send(imageSource)
  }
  const processFrames = () =>
    (animationFrameId ??= requestAnimationFrame(() => {
      animationFrameId = null

      const v = video?.getVideo()
      const dimensions = video?.getDimensions()

      if (!v || !isVideoFeedActive || !dimensions) return

      processFrame({ imageSource: v, dimensions })
        .then(() => processFrames())
        .catch((e) => {
          isVideoFeedActive = false
          onError(e)
        })
    }))

  $effect(() => {
    const check = [checkFront, checkLeft, checkRight][phase]

    if (!check) {
      stage = STAGE.CAPTURING_DONE
      return
    }

    const problem = check({ detections, settings: $applicationStore.config.settings })

    if (phase < 4 && !problem) return capture()

    cancelCapture()

    if (stage !== problem) stage = problem
  })

  $effect(() => {
    if (phase >= 3) setTimeout(() => onComplete(), COMPLETE_DELAY)
  })

  $effect(() => {
    if (isLoading) analytics.loading({ timestamp: Date.now() })
  })
  $effect(() => {
    if (isVideoFeedActive) processFrames()
  })
</script>

<section
  class={[
    "live-video", // anchor for external customization
    "relative size-full",
  ]}
>
  <VideoFeed
    src={stream}
    mirrored={facing === "front"}
    onStart={() => (isVideoFeedActive = true)}
    onStop={() => (isVideoFeedActive = false)}
    bind:this={video}
  />

  {#if isLoading}
    <LoadingSplashScreen class="!absolute left-0 top-0 z-10" progress={loading} />
  {:else if isVideoFeedActive}
    <QualityCheckStages
      class="!absolute left-0 top-0 z-0 size-full"
      {stage}
      {detections}
      {facing}
      onChange={(stage) => sendAnalytics({ stage, detections })}
    />
  {/if}
</section>
